import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Importações das traduções - Common
import commonPtBR from '../locales/common/pt-BR.json'
import commonEnUS from '../locales/common/en-US.json'
import commonEsES from '../locales/common/es-ES.json'

// Importações das traduções - Footer
import footerTranslations from '../locales/common/footer.json'

// Importações das traduções - Components
import componentsPtBR from '../locales/components/ui/pt-BR.json'
import componentsEnUS from '../locales/components/ui/en-US.json'
import componentsEsES from '../locales/components/ui/es-ES.json'

const resources = {
  'pt-BR': {
    common: commonPtBR,
    footer: footerTranslations['pt-BR'],
    components: componentsPtBR
  },
  'en-US': {
    common: commonEnUS,
    footer: footerTranslations['en-US'],
    components: componentsEnUS
  },
  'es-ES': {
    common: commonEsES,
    footer: footerTranslations['es-ES'],
    components: componentsEsES
  }
}

// Configuração de detecção de idioma
const detectionOptions = {
  // Ordem de prioridade para detecção do idioma
  // Path detection is handled manually in LocalizedRoutes
  order: ['localStorage', 'navigator', 'htmlTag'],

  // Armazenar idioma selecionado no localStorage
  caches: ['localStorage'],

  // Chave para armazenar no localStorage
  lookupLocalStorage: 'i18nextLng',

  // Idiomas suportados
  checkWhitelist: true
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,

    // Idioma padrão
    fallbackLng: 'en-US',
    lng: 'pt-BR', // Idioma inicial padrão

    // Idiomas suportados
    supportedLngs: ['pt-BR', 'en-US', 'es-ES'],

    // Configuração de detecção
    detection: detectionOptions,

    // Namespaces padrão
    defaultNS: 'common',
    ns: ['common', 'footer', 'components'],

    // Configurações de interpolação
    interpolation: {
      escapeValue: false // React já faz sanitização
    },

    // Configurações de desenvolvimento
    debug: false,

    // Configurações de carregamento
    load: 'currentOnly', // Carregar apenas o idioma atual

    // Configurações de plural
    pluralSeparator: '_',

    // Configurações de contexto
    contextSeparator: '_'
  })

// Função para carregar namespace dinamicamente
export const loadNamespace = async (language, namespace) => {
  if (i18n.hasResourceBundle(language, namespace)) {
    return Promise.resolve()
  }

  try {
    let resource

    // Carregar baseado no namespace e idioma
    switch (namespace) {
      case 'pages/home':
        resource = await import(`../locales/pages/home/${language}.json`)
        break
      case 'pages/about':
        resource = await import(`../locales/pages/about/${language}.json`)
        break
      case 'pages/features':
        resource = await import(`../locales/pages/features/${language}.json`)
        break
      case 'pages/pricing':
        resource = await import(`../locales/pages/pricing/${language}.json`)
        break
      case 'pages/cases':
        resource = await import(`../locales/pages/cases/${language}.json`)
        break
      case 'pages/contact':
        resource = await import(`../locales/pages/contact/${language}.json`)
        break
      case 'pages/platform':
        resource = await import(`../locales/pages/platform/${language}.json`)
        break
      case 'pages/workflow-demo':
        resource = await import(`../locales/pages/workflow-demo/${language}.json`)
        break
      case 'pages/small-business':
        resource = await import(`../locales/pages/small-business/${language}.json`)
        break
      case 'pages/api':
        resource = await import(`../locales/pages/api/${language}.json`)
        break
      case 'pages/hr':
        resource = await import(`../locales/pages/hr/${language}.json`)
        break
      case 'pages/icp-brasil':
        resource = await import(`../locales/pages/icp-brasil/${language}.json`)
        break
      case 'pages/law':
        resource = await import(`../locales/pages/law/${language}.json`)
        break
      case 'pages/real-estate':
        resource = await import(`../locales/pages/real-estate/${language}.json`)
        break
      case 'pages/healthcare':
        resource = await import(`../locales/pages/healthcare/${language}.json`)
        break
      case 'components/forms':
        resource = await import(`../locales/components/forms/${language}.json`)
        break
      case 'components/calculators':
        resource = await import(`../locales/components/calculators/${language}.json`)
        break
      case 'data/features':
        resource = await import(`../locales/data/features/${language}.json`)
        break
      case 'data/pricing':
        resource = await import(`../locales/data/pricing/${language}.json`)
        break
      case 'pages/security':
        resource = await import(`../locales/pages/security/${language}.json`)
        break
      case 'data/faq':
        resource = await import(`../locales/data/faq/${language}.json`)
        break
      default:
        console.warn(`Namespace ${namespace} não encontrado`)
        return Promise.resolve()
    }

    // Adicionar resource bundle ao i18n
    i18n.addResourceBundle(language, namespace, resource.default, true, true)

    return Promise.resolve()
  } catch (error) {
    console.error(`Erro ao carregar namespace ${namespace} para idioma ${language}:`, error)
    return Promise.resolve()
  }
}

// Função para detectar idioma inicial baseado na URL e browser
export const detectInitialLanguage = () => {
  // 1. Verificar URL path primeiro (para SEO e links diretos)
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname
    if (pathname.startsWith('/en/') || pathname === '/en') {
      return 'en-US'
    }
    if (pathname.startsWith('/es/') || pathname === '/es') {
      return 'es-ES'
    }
  }

  // 2. Verificar localStorage (apenas se estiver em path português)
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname
    if (!pathname.startsWith('/en') && !pathname.startsWith('/es')) {
      const savedLanguage = localStorage.getItem('i18nextLng')
      if (savedLanguage && ['pt-BR', 'en-US', 'es-ES'].includes(savedLanguage)) {
        return savedLanguage
      }
    }
  }

  // 3. Verificar idioma do navegador
  const browserLanguage = navigator.language || navigator.languages[0]

  // Mapear idiomas do browser para nossos idiomas suportados
  const languageMap = {
    'pt': 'pt-BR',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-BR',
    'en': 'en-US',
    'en-US': 'en-US',
    'en-GB': 'en-US',
    'es': 'es-ES',
    'es-ES': 'es-ES',
    'es-MX': 'es-ES',
    'es-AR': 'es-ES'
  }

  // Tentar mapear idioma exato primeiro
  if (languageMap[browserLanguage]) {
    return languageMap[browserLanguage]
  }

  // Tentar mapear apenas o código do idioma (ex: 'pt' de 'pt-BR')
  const languageCode = browserLanguage.split('-')[0]
  if (languageMap[languageCode]) {
    return languageMap[languageCode]
  }

  // 4. Fallback para português (idioma padrão do site)
  return 'pt-BR'
}

// Função para mudar idioma
export const changeLanguage = async (newLanguage) => {
  try {
    await i18n.changeLanguage(newLanguage)
    localStorage.setItem('i18nextLng', newLanguage)

    // Recarregar página para aplicar mudanças de SEO e URLs se necessário
    // window.location.reload()

    return true
  } catch (error) {
    console.error('Erro ao mudar idioma:', error)
    return false
  }
}

// Aplicar idioma inicial detectado
const initialLanguage = detectInitialLanguage()
if (initialLanguage !== i18n.language) {
  i18n.changeLanguage(initialLanguage)
}

export default i18n
